import { Fragment, useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { Button, Icon } from "@/deps/ui-library";
import type { ReportExport } from "@/api/types";
import { REPORTS, REPORT_GROUPS, readStarred, writeStarred, type ReportId } from "./catalog";
import { DownloadMenu } from "./DownloadMenu";
import { useTaxWords } from "@/app/session";

/** The rail of reports beside whichever one is open. */
export function ReportRail({ active }: { active?: ReportId }) {
  const say = useTaxWords();
  const itemClass = (on: boolean) =>
    `flex items-center gap-2 rounded-r-md border-l-2 px-3 py-1.5 text-sm ${
      on ? "border-primary text-fg font-semibold" : "text-fg-muted hover:text-fg border-transparent"
    }`;
  return (
    <nav aria-label="Reports" className="hidden w-52 shrink-0 flex-col gap-0.5 lg:flex">
      <Link to="/reports" className={itemClass(active === undefined)}>
        <Icon name="LayoutGrid" size="sm" />
        All reports
      </Link>
      {REPORT_GROUPS.map((group) => (
        <Fragment key={group}>
          <div className="text-fg-muted mt-3 mb-1 px-3 text-xs font-medium">{group}</div>
          {REPORTS.filter((report) => report.group === group).map((report) => (
            <Link
              key={report.id}
              to={report.to as never}
              className={itemClass(active === report.id)}
            >
              <Icon name={report.icon as never} size="sm" />
              {say(report.label)}
            </Link>
          ))}
        </Fragment>
      ))}
    </nav>
  );
}

/**
 * The frame of every report: the rail, the title, the period and nothing else under it, a star, and
 * Download. There is no Print, and no PDF: a report leaves the screen as Excel or CSV (ADR-0032).
 */
export function ReportFrame({
  id,
  title,
  period,
  exportable,
  children,
}: {
  id: ReportId;
  title: string;
  period?: string;
  /** What Download writes. Absent while there is nothing to write. */
  exportable?: () => ReportExport;
  children: ReactNode;
}) {
  const [starred, setStarred] = useState(readStarred);
  const on = starred.includes(id);

  const toggleStar = () => {
    const next = on ? starred.filter((s) => s !== id) : [...starred, id];
    setStarred(next);
    writeStarred(next);
  };

  return (
    <div className="flex items-start gap-6">
      <ReportRail active={id} />
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-fg text-xl font-semibold tracking-[-0.02em]">{title}</h1>
            {period ? <p className="text-fg-muted mt-1 text-xs">{period}</p> : null}
          </div>
          <div className="flex items-center gap-2">
            <Button
              colorScheme="default"
              variant="ghost"
              size="sm"
              leftIcon="Star"
              aria-pressed={on}
              onClick={toggleStar}
            >
              {on ? "Starred" : "Star"}
            </Button>
            <DownloadMenu title={title} period={period} exportable={exportable} />
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}
